// Sync queue — flushes pending operations to the backend when online.

import { toast } from "sonner";
import {
  countPendingOperations,
  enqueueOperation,
  getPendingOperations,
  removePendingOperation,
} from "@/lib/offlineDb";
import { operationsApi } from "@/lib/api";

let flushing = false;
let listeners = new Set();

function notify() {
  countPendingOperations()
    .then((n) => {
      listeners.forEach((cb) => {
        try {
          cb({ pending: n });
        } catch (e) {
          console.warn("[syncQueue] listener threw", e);
        }
      });
    })
    .catch((e) => {
      console.warn("[syncQueue] notify count failed", e);
    });
}

export function subscribeQueue(cb) {
  listeners.add(cb);
  notify();
  return () => listeners.delete(cb);
}

export async function getPendingCount() {
  try {
    return await countPendingOperations();
  } catch {
    return 0;
  }
}

/** Queue an operation for later sync. Always succeeds (writes to IndexedDB). */
export async function queueOperation(payload) {
  const id = await enqueueOperation(payload);
  notify();
  return id;
}

/** Try to send a single pending operation by id. Returns true on success. */
export async function syncOne(id) {
  if (!navigator.onLine) return false;
  const items = await getPendingOperations();
  const item = items.find((i) => i.id === id);
  if (!item) return false;
  try {
    await operationsApi.create(item.body);
    await removePendingOperation(id);
    notify();
    return true;
  } catch (err) {
    return false;
  }
}

/** Remove a queued operation without sending it. */
export async function discardOne(id) {
  await removePendingOperation(id);
  notify();
}

/** Try to send all pending operations. Returns the number successfully sent. */
export async function flushQueue() {
  if (flushing) return 0;
  if (!navigator.onLine) return 0;
  flushing = true;

  let sent = 0;
  try {
    const items = await getPendingOperations();
    for (const item of items) {
      try {
        await operationsApi.create(item.body);
        await removePendingOperation(item.id);
        sent++;
      } catch (err) {
        // Stop on first failure (likely network or backend down) to avoid
        // hammering the API. We'll retry later.
        break;
      }
    }
  } finally {
    flushing = false;
    notify();
  }

  if (sent > 0) {
    toast.success(
      `${sent} opération${sent > 1 ? "s" : ""} synchronisée${sent > 1 ? "s" : ""}`,
      { description: "Les bordereaux en attente ont été envoyés au serveur." },
    );
  }
  return sent;
}

/** Hook into online/offline events to auto-flush. */
export function installSyncListeners() {
  window.addEventListener("online", () => {
    flushQueue();
  });
  // Try once on load too
  if (navigator.onLine) {
    setTimeout(() => flushQueue(), 1500);
  }
}
